import { useState, useEffect } from 'react'
import { Box, Typography, Tabs, Tab, Alert } from '@mui/material'
import {
  BarChart,
  Factory,
  Public,
  CompareArrows,
} from '@mui/icons-material'
import { usePlantContext } from '../hooks/usePlantContext'
import { api } from '../services/api'
import LoadingState from '../components/common/LoadingState'
import ExportMenu from '../components/common/ExportMenu'
import PlantRollup from '../components/reports/PlantRollup'
import CrossPlantSummary from '../components/reports/CrossPlantSummary'
import ScenarioComparison from '../components/reports/ScenarioComparison'
import PlanComparison from './PlanComparison'

const TABS = [
  { key: 'rollup', label: 'Plant Rollup', icon: Factory, Component: PlantRollup },
  // FAR-69: 'Budget vs Plan' compares ECs only; hidden with ECs until changeover data exists.
  { key: 'cross', label: 'Cross-Plant', icon: Public, Component: CrossPlantSummary },
  { key: 'comparison', label: 'Plan Comparison', icon: CompareArrows, Component: PlanComparison },
]

export default function Reports() {
  const { plant } = usePlantContext()
  const [tab, setTab] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [rollupData, setRollupData] = useState(null)
  const [crossPlantData, setCrossPlantData] = useState(null)

  useEffect(() => {
    let cancelled = false
    async function fetchReports() {
      setLoading(true)
      setError(null)
      try {
        const [rollup, crossPlant] = await Promise.all([
          api.get(`/reports/rollup?plant=${plant.plantCode}`).catch(() => null),
          api.get('/reports/crossplant').catch(() => null),
        ])
        if (cancelled) return
        setRollupData(rollup)
        setCrossPlantData(crossPlant)
      } catch (err) {
        if (cancelled) return
        setError(err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchReports()
    return () => { cancelled = true }
  }, [plant.plantCode])

  // Transform API responses to match component expectations
  const transformedRollup = (() => {
    if (!rollupData?.lines || !Array.isArray(rollupData.lines) || rollupData.lines.length === 0) return null
    const result = {}
    for (const line of rollupData.lines) {
      const months = ['2026-04','2026-05','2026-06','2026-07','2026-08','2026-09','2026-10','2026-11','2026-12','2027-01','2027-02','2027-03']
      result[line.line || line.Line] = months.map(m => {
        const md = line.monthlyData?.[m] || line.MonthlyData?.[m] || {}
        return { cases: md.cases || 0, ecs: md.ecs || 0, hours: md.hours || 0 }
      })
    }
    return Object.keys(result).length > 0 ? result : null
  })()

  // Cross-plant API returns aggregate totals, not per-month breakdowns
  // Fall back to demo data until per-month cross-plant data is available
  const transformedCross = null

  const dataForTab = {
    rollup: transformedRollup,
    cross: transformedCross,
    scenario: null,
  }

  const ActiveComponent = TABS[tab].Component
  const activeData = dataForTab[TABS[tab].key]

  if (loading) {
    return <LoadingState message="Loading reports…" />
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <BarChart color="primary" sx={{ fontSize: 22 }} />
        <Typography variant="h3" sx={{ flex: 1 }}>Reports</Typography>
        <ExportMenu />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {plant.name} — FY 2026-27
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {TABS.map((t) => (
          <Tab
            key={t.key}
            icon={<t.icon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={t.label}
            sx={{ minHeight: 48, textTransform: 'none' }}
          />
        ))}
      </Tabs>

      <Box sx={{ overflowX: 'auto' }}>
        <ActiveComponent data={activeData} />
      </Box>
    </Box>
  )
}
